import React from 'react';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell } from 'utils/groupLabel';
import styles from './StartListsEditor.module.css';

// Format milliseconds to proper time format
const formatTime = (milliseconds) => {
  if (!milliseconds || milliseconds === 'N/A') return 'N/A';
  
  const totalSeconds = milliseconds / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes > 0) {
    return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
  } else {
    return seconds.toFixed(2);
  }
};

const AthletesPanel = ({
  availableAthletes,
  selectedAthlete,
  sortBy,
  onSortChange,
  onAthleteSelect
}) => {
  // The badge beside an athlete reads "11", or "Y6" for a club that groups by year level, and the sort choice
  // is named to match.
  const { groupingBasis, words } = useGroupLabels();

  const sortedAthletes = [...availableAthletes]
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return nameA.localeCompare(nameB);
        
        case 'age':
          const ageA = a.age || 0;
          const ageB = b.age || 0;
          return ageB - ageA;
        
        case 'bib':
          // NEW: Sort by bib number (athleteNumber) - lowest to highest
          const bibA = a.athleteNumber ? parseInt(a.athleteNumber) : Infinity;
          const bibB = b.athleteNumber ? parseInt(b.athleteNumber) : Infinity;
          return bibA - bibB;
        
        case 'sb':
        default:
          const sbA = a.seasonBest ? parseFloat(a.seasonBest) : Infinity;
          const sbB = b.seasonBest ? parseFloat(b.seasonBest) : Infinity;
          return sbA - sbB;
      }
    });

  return (
    <div className={styles.athletesPanel}>
      <div className={styles.athletesHeader}>
        Available Athletes ({availableAthletes.length})
      </div>

      <select
        value={sortBy}
        onChange={(e) => onSortChange(e.target.value)}
        className={styles.sortSelect}
      >
        <option key="sb" value="sb">Sort: SB ▼</option>
        <option key="name" value="name">Sort: Name ▼</option>
        <option key="age" value="age">{`Sort: ${words.value} ▼`}</option>
        <option key="bib" value="bib">Sort: Bib ▼</option>
      </select>

      <div className={styles.athletesList}>
        {sortedAthletes.map((athlete) => (
          <div
            key={athlete.athleteID}
            onClick={() => onAthleteSelect(athlete)}
            className={`${styles.athleteCard} ${selectedAthlete?.athleteID === athlete.athleteID ? styles.athleteCardSelected : ''}`}
          >
            <div className={styles.athleteCardRow1}>
              <span className={styles.athleteName}>
                {athlete.firstName} {athlete.lastName}
              </span>
              <span className={styles.athleteId}>{athlete.athleteNumber}</span>
            </div>
            <div className={styles.athleteCardRow2}>
              <span className={styles.badge}>{formatGroupCell(groupingBasis, athlete.age)}</span>
              <span className={styles.badge}>{athlete.gender}</span>
              <span className={styles.sbLabel}>SB:</span>
              <span className={styles.sbValue}>{formatTime(athlete.seasonBest)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AthletesPanel;